/**
 * useReloadDiagnostics — a **dev/preview-only** hook that logs why a page
 * appears to have "refreshed", so we can distinguish between:
 *
 *   • a real browser reload (F5 / ⌘R / hard reload)
 *   • a Vite HMR update or forced full reload
 *   • a React route navigation
 *   • a React remount inside the tree
 *
 * Never runs in production. Never fires more than once per event. Output
 * is a single ``console.info`` line prefixed ``[reload-diag]`` so it is
 * easy to grep in browser DevTools.
 */
import { useEffect } from "react";

const PROD = import.meta.env.PROD;

interface NavigationEntry {
  type?: string;
}

function readNavType(): string {
  try {
    const entries = performance.getEntriesByType?.("navigation") as NavigationEntry[] | undefined;
    return entries?.[0]?.type ?? "unknown";
  } catch {
    return "unknown";
  }
}

export function useReloadDiagnostics(): void {
  useEffect(() => {
    if (PROD) return;

    const navType = readNavType();
    const isReload = navType === "reload";
    const startedAt = new Date().toISOString();

    // eslint-disable-next-line no-console
    console.info(
      "[reload-diag] mount",
      JSON.stringify({
        at: startedAt,
        path: window.location.pathname,
        navigationType: navType,
        looksLikeReload: isReload,
      }),
    );

    // Vite exposes an HMR client; hook into its events to attribute reload cause.
    const hot = (import.meta as unknown as { hot?: { on?: (event: string, cb: (payload: unknown) => void) => void } }).hot;
    if (hot?.on) {
      hot.on("vite:beforeFullReload", (payload) => {
        // eslint-disable-next-line no-console
        console.info("[reload-diag] vite:beforeFullReload", payload);
      });
      hot.on("vite:beforeUpdate", (payload) => {
        // eslint-disable-next-line no-console
        console.info("[reload-diag] vite:beforeUpdate", payload);
      });
      hot.on("vite:invalidate", (payload) => {
        // eslint-disable-next-line no-console
        console.info("[reload-diag] vite:invalidate", payload);
      });
      hot.on("vite:error", (payload) => {
        // eslint-disable-next-line no-console
        console.info("[reload-diag] vite:error", payload);
      });
      hot.on("vite:ws:disconnect", (payload) => {
        // eslint-disable-next-line no-console
        console.info("[reload-diag] vite:ws:disconnect", payload);
      });
      hot.on("vite:ws:connect", (payload) => {
        // eslint-disable-next-line no-console
        console.info("[reload-diag] vite:ws:connect", payload);
      });
    }

    const onBeforeUnload = () => {
      // eslint-disable-next-line no-console
      console.info(
        "[reload-diag] beforeunload",
        JSON.stringify({ at: new Date().toISOString(), path: window.location.pathname }),
      );
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);
}
